import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useMasterData } from "../../../hooks/useMasterData";
import { studentsApi } from "../students/api";
import {
  studentComplaintsApi,
  StudentComplaintFilters,
  StudentComplaintListItem,
} from "./studentComplaintsApi";
import {
  GrievancePriority,
  GrievanceStatus,
  GrievanceType,
  GrievanceCategory,
} from "../grievance/grievanceApi";

interface FilterState extends StudentComplaintFilters {
  page: number;
  limit: number;
}

interface StudentOption {
  id: string;
  name: string;
  admissionNumber: string;
}

const statusColor: Record<
  GrievanceStatus,
  "default" | "primary" | "warning" | "success" | "error"
> = {
  OPEN: "error",
  IN_PROGRESS: "warning",
  RESOLVED: "success",
  CLOSED: "default",
};

const priorityColor: Record<
  GrievancePriority,
  "default" | "primary" | "warning" | "success" | "error"
> = {
  LOW: "default",
  MEDIUM: "primary",
  HIGH: "error",
};

const StudentComplaintsList: React.FC = () => {
  const navigate = useNavigate();
  const { masters, fetchMasters, fetchSections } = useMasterData();

  const [filters, setFilters] = useState<FilterState>({
    academicSessionId: "",
    classId: "",
    sectionId: "",
    status: undefined,
    priority: undefined,
    studentId: "",
    type: undefined,
    category: undefined,
    ticketNumber: "",
    fromDate: "",
    toDate: "",
    page: 0,
    limit: 20,
  });

  const [students, setStudents] = useState<StudentOption[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<StudentComplaintListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    highPriority: 0,
  });

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get("/system-settings/academic-sessions");
      setSessions(res.data || []);
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    fetchMasters();
    loadSessions();
  }, [fetchMasters, loadSessions]);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then((secs) => setSections(secs || []));
    } else {
      setSections([]);
    }
  }, [filters.classId, fetchSections]);

  const loadStudents = useCallback(async () => {
    if (!filters.classId || !filters.sectionId) {
      setStudents([]);
      return;
    }
    try {
      const res = await studentsApi.getAll({
        page: 1,
        limit: 100,
        classId: filters.classId,
        sectionId: filters.sectionId,
      });
      const list = res.data || [];
      setStudents(
        list.map((s: any) => ({
          id: s.id,
          name: `${s.firstName} ${s.lastName}`,
          admissionNumber: s.admissionNumber,
        })),
      );
    } catch {
      setStudents([]);
    }
  }, [filters.classId, filters.sectionId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    try {
      const response = await studentComplaintsApi.getAll(
        {
          academicSessionId: filters.academicSessionId || undefined,
          classId: filters.classId || undefined,
          sectionId: filters.sectionId || undefined,
          status: filters.status || undefined,
          priority: filters.priority || undefined,
          studentId: filters.studentId || undefined,
          type: filters.type || undefined,
          category: filters.category || undefined,
          ticketNumber: filters.ticketNumber || undefined,
          fromDate: filters.fromDate || undefined,
          toDate: filters.toDate || undefined,
        },
        filters.page + 1,
        filters.limit,
      );
      setItems(response.data);
      setTotal(response.meta.total);
      setStats(response.meta.stats);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 0,
    }));
  };

  const handleChangePage = (_: any, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilters((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleQuickClose = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to mark this complaint as Closed?",
      )
    ) {
      return;
    }
    try {
      await studentComplaintsApi.updateStatus(id, {
        status: "CLOSED",
      });
      fetchComplaints();
    } catch {
    }
  };

  const isEscalated = (row: StudentComplaintListItem) => {
    if (row.status !== "OPEN") {
      return false;
    }
    const created = new Date(row.createdAt).getTime();
    const diff = Date.now() - created;
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return diff > sevenDaysMs;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight={500}>
          Student Complaints (All)
        </Typography>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Total Complaints
              </Typography>
              <Typography variant="h6">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Open
              </Typography>
              <Typography variant="h6">{stats.open}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                In Progress
              </Typography>
              <Typography variant="h6">{stats.inProgress}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Resolved
              </Typography>
              <Typography variant="h6">{stats.resolved}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Closed
              </Typography>
              <Typography variant="h6">{stats.closed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                High Priority
              </Typography>
              <Typography variant="h6">{stats.highPriority}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Session"
              size="small"
              value={filters.academicSessionId || ""}
              onChange={(e) =>
                handleFilterChange("academicSessionId", e.target.value || "")
              }
            >
              <MenuItem value="">All</MenuItem>
              {sessions.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Class"
              size="small"
              value={filters.classId || ""}
              onChange={(e) =>
                handleFilterChange("classId", e.target.value || "")
              }
            >
              <MenuItem value="">All</MenuItem>
              {masters.classes.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Section"
              size="small"
              value={filters.sectionId || ""}
              onChange={(e) =>
                handleFilterChange("sectionId", e.target.value || "")
              }
            >
              <MenuItem value="">All</MenuItem>
              {sections.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Student"
              size="small"
              value={filters.studentId || ""}
              onChange={(e) =>
                handleFilterChange("studentId", e.target.value || "")
              }
            >
              <MenuItem value="">All</MenuItem>
              {students.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.admissionNumber} - {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              size="small"
              value={filters.status || ""}
              onChange={(e) =>
                handleFilterChange(
                  "status",
                  (e.target.value || "") as GrievanceStatus | "",
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              {["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"].map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace("_", " ")}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Priority"
              size="small"
              value={filters.priority || ""}
              onChange={(e) =>
                handleFilterChange(
                  "priority",
                  (e.target.value || "") as GrievancePriority | "",
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              {["LOW", "MEDIUM", "HIGH"].map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Type"
              size="small"
              value={filters.type || ""}
              onChange={(e) =>
                handleFilterChange(
                  "type",
                  (e.target.value || "") as GrievanceType | "",
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="GRIEVANCE">Grievance</MenuItem>
              <MenuItem value="SUGGESTION">Suggestion</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Category"
              size="small"
              value={filters.category || ""}
              onChange={(e) =>
                handleFilterChange(
                  "category",
                  (e.target.value || "") as GrievanceCategory | "",
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACADEMIC">Academic</MenuItem>
              <MenuItem value="DISCIPLINE">Discipline</MenuItem>
              <MenuItem value="FACILITY">Facility</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Ticket No"
              size="small"
              value={filters.ticketNumber || ""}
              onChange={(e) =>
                handleFilterChange("ticketNumber", e.target.value || "")
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="From Date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate || ""}
              onChange={(e) =>
                handleFilterChange("fromDate", e.target.value || "")
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="To Date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.toDate || ""}
              onChange={(e) =>
                handleFilterChange("toDate", e.target.value || "")
              }
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticket No</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Admission No</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={
                    isEscalated(row)
                      ? { backgroundColor: "rgba(244, 67, 54, 0.04)" }
                      : undefined
                  }
                >
                  <TableCell>{row.ticketNumber}</TableCell>
                  <TableCell>
                    {row.student
                      ? `${row.student.firstName} ${row.student.lastName}`
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {row.student ? row.student.admissionNumber : "-"}
                  </TableCell>
                  <TableCell>{row.class ? row.class.name : "-"}</TableCell>
                  <TableCell>{row.section ? row.section.name : "-"}</TableCell>
                  <TableCell>
                    {row.type === "GRIEVANCE" ? "Grievance" : "Suggestion"}
                  </TableCell>
                  <TableCell>{row.category}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.priority}
                      color={priorityColor[row.priority]}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status.replace("_", " ")}
                      color={statusColor[row.status]}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(row.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/scholar-register/complaints/${row.id}`)
                      }
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/scholar-register/complaints/${row.id}/edit`)
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleQuickClose(row.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box textAlign="center" py={3}>
                      <Typography color="textSecondary">
                        No complaints found.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={filters.page}
          onPageChange={handleChangePage}
          rowsPerPage={filters.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>
    </Container>
  );
};

export default StudentComplaintsList;

